import { isEventVisibleTo, type CalendarEvent } from '@awwyeah/shared';

/**
 * The calendar's WS-15 merge, pulled out of `useCalendarStore` so it is testable without
 * a renderer — this is the function that decides what a member actually sees, and it is
 * worth being able to assert on directly.
 *
 * Two inputs, two very different guarantees:
 *
 *  - `familyEvents` come from the shared `calendar` doc, which everybody can open. They
 *    are filtered by `isEventVisibleTo`, which is a **display** filter and nothing more.
 *  - `personalEvents` come from `calendar:user:<me>`, which the sync hub refuses to open
 *    for anybody else. No filtering is needed or applied: if you can read the doc, they
 *    are yours.
 *
 * An id present in both wins as personal — same tie-break as the lists registry merge,
 * and for the same reason (the narrower answer is the safe one to show).
 */
export function mergeVisibleEvents(
  familyEvents: readonly CalendarEvent[],
  personalEvents: readonly CalendarEvent[],
  meId: string | null,
): CalendarEvent[] {
  const personalIds = new Set(personalEvents.map((event) => event.id));
  const visibleFamily = familyEvents.filter(
    (event) => !personalIds.has(event.id) && isEventVisibleTo(event, meId),
  );
  return [...visibleFamily, ...personalEvents];
}
